import { Box, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import React, { Fragment, useEffect, useState } from "react";
import { EErrorMessages } from "../constants/api-constant";
import { CommonFunctions } from "../utils/CommonFunctions";
import Heading from "./Heading";

export interface IUploadSection {
  inputFiles: {
    title: string;
    format: string;
    size: number;
    accept: string;
    error: string;
  };
  submitted: boolean;
  headers: string[];
  setData: (fileName: string, data: any) => void;
  setError: (error: string) => void;
}

function UploadSection({
  inputFiles,
  submitted,
  headers,
  setData,
  setError,
}: IUploadSection) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<any | null>(null);

  const validateFile = (file: File) => {
    const allowedTypes = ["text/csv"];
    const maxSize = inputFiles.size * 1024 * 1024;

    if (!allowedTypes.includes(file.type) && file.size > maxSize) {
      setFileError(EErrorMessages.InvalidandExceedsSize);
      return false;
    }
    if (!allowedTypes.includes(file.type)) {
      setFileError(EErrorMessages.InvalidFormat);
      return false;
    }
    if (file.size > maxSize) {
      setFileError(EErrorMessages.ExceedsSize);
      return false;
    }
    return true;
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    file && handleFileUpload(file);
  };

  const handleDragover = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleFileChange = async (event: any) => {
    const file = event.target.files[0];
    event.target.value = "";
    file && handleFileUpload(file);
  };

  const handleFileUpload = async (file: File | null | any) => {
    setSelectedFile(null);
    setFileError("");
    setError("");
    setData("", null);
    if (!validateFile(file)) return;
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension === "csv") {
      const dataObjects = await CommonFunctions.importCSVData(file, headers);
      if (dataObjects === "InvalidFormat") {
        setFileError(EErrorMessages.InvalidFormat);
        return;
      }
      setSelectedFile(file);
      setData(file.name, dataObjects);
    }
  };

  useEffect(() => {
    submitted && setFileError(inputFiles.error);
    !submitted && setFileError("");
  }, [submitted, inputFiles.error]);

  return (
    <Fragment>
      <Box
        data-testid="upload-dropzone"
        className={`uploadSection ${fileError ? "border-danger" : ""} `}
        onClick={() => document.getElementById("file-input")?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragover}
        onDragLeave={() => setIsDragging(!isDragging)}
      >
        <span className="material-symbols-outlined upload-icon">
          upload_file
        </span>

        <Heading
          text={`${inputFiles.title}`}
          classes="upload-text"
          font="16px"
          isBold={true}
        />
        <Heading
          text={`Supported File Formats: ${inputFiles.format} (Max size: ${inputFiles.size} MB per file) `}
          type="subtitle1"
          isBold={true}
        />

        <input
          id="file-input"
          data-testid="file-input"
          type="file"
          accept={`${inputFiles.accept}`}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>
      {selectedFile && selectedFile.name && (
        <Box className="divAlign">
          <Tooltip title={selectedFile.name}>
            <Box className="divAlign main-info-color">
              <span
                className="material-symbols-outlined pr-10"
                style={{ fontSize: "16px" }}
              >
                attachment
              </span>
              <Heading
                text={CommonFunctions.truncateText(selectedFile.name, 50)}
                font="14px"
              />
            </Box>
          </Tooltip>
          <IconButton
            data-testid="close-icon"
            onClick={() => {
              setSelectedFile(null);
              setData("", null);
            }}
          >
            <span className="material-symbols-outlined close-icon">close</span>
          </IconButton>
        </Box>
      )}
      {fileError && (
        <Heading
          data-testid="heading-Error"
          color="error"
          font="16px"
          text={fileError}
        />
      )}
    </Fragment>
  );
}

export default UploadSection;
